"use client";

// ============================================================
// CLUBHUB – Dokumente: der Filterkopf der Ablage
// ============================================================
// Derselbe Kopf wie auf jeder Liste (`liste/filterkopf`): Suche
// links, Zaehler rechts — die Zaehler SIND der Filter. Dazu die
// Sortierung und der Wechsel zwischen Raster und Liste.
//
// Die Suche geht ueber ALLE Ordner, in denen man etwas sehen darf
// (ClubDesk suchte nur im offenen Laufwerk). Ist ein Ordner offen,
// laesst sie sich auf ihn beschraenken.
//
// `Dateisuche` sieht aus wie `Suchfeld`, schreibt aber ueber einen
// Rueckruf statt ueber den Router: gefiltert wird hier im Browser.
// ============================================================

import { LayoutGrid, List, Search, X } from "lucide-react";
import { Select } from "@/components/ui/select";
import { noAutocorrect } from "@/components/ui/no-autocorrect";
import {
  Filterkopf,
  Fuge,
  Luecke,
  PLAETTCHEN,
  PLAETTCHEN_AN,
  PLAETTCHEN_AUS,
  Schiene,
  Zaehlknopf,
  Zaehlschiene,
} from "@/components/liste";
import { cn } from "@/lib/utils";
import { formatDecimal } from "@/lib/number-format";
import { ART_REIHE, DATEIARTEN, SORTIERUNGEN, type DateiArt } from "@/lib/dokumente/format";
import { useAblage } from "./ablage-kontext";
import type { Darstellung } from "./adresse";

export function Dateisuche({
  wert,
  onWert,
  platzhalter,
  beschriftung,
}: {
  wert: string;
  onWert: (q: string) => void;
  platzhalter: string;
  beschriftung: string;
}) {
  return (
    <div className="relative max-md:min-w-0 max-md:shrink max-md:grow max-md:basis-48">
      <Search className="text-text-tertiary pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2" aria-hidden />
      <input
        type="search"
        {...noAutocorrect}
        value={wert}
        onChange={(e) => onWert(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape" && wert) {
            e.preventDefault();
            onWert("");
          }
        }}
        placeholder={platzhalter}
        aria-label={beschriftung}
        className={cn(
          "border-border bg-bg-primary text-body-sm text-text-primary placeholder:text-text-tertiary duration-normal ease-standard hover:border-border-strong focus:border-accent/40 focus:ring-accent/30 h-9 rounded-lg border pr-7 pl-8 transition-[width,border-color] focus:ring-2 focus:outline-none [&::-webkit-search-cancel-button]:hidden",
          "w-56 focus:w-72 max-md:w-full max-md:focus:w-full",
          wert && "md:w-72",
        )}
      />
      {wert && (
        <button
          type="button"
          onClick={() => onWert("")}
          aria-label="Suche leeren"
          className="text-text-tertiary duration-fast hover:bg-bg-tertiary hover:text-text-primary absolute top-1/2 right-1.5 grid h-6 w-6 -translate-y-1/2 place-items-center rounded transition-colors"
        >
          <X className="h-3 w-3" aria-hidden />
        </button>
      )}
    </div>
  );
}

export function AblageFilterkopf({
  artZaehler,
  gesamt,
  ordnerName,
  darstellung,
  trefferUeberall,
  trefferHier,
}: {
  artZaehler: Map<DateiArt, number>;
  gesamt: number;
  /** Der offene Ordner — fuer „nur hier suchen“. */
  ordnerName: string | null;
  darstellung: Darstellung;
  trefferUeberall: number;
  trefferHier: number;
}) {
  const { ansicht, setzen } = useAblage();

  return (
    <Filterkopf
      oben={
        <>
          <Dateisuche
            wert={ansicht.q}
            onWert={(q) => setzen({ q: q || null, datei: null })}
            platzhalter="In allen Ordnern suchen"
            beschriftung="Dateien und Ordner durchsuchen"
          />

          {ansicht.q && ordnerName && (
            <Schiene>
              <button
                type="button"
                aria-pressed={!ansicht.nurHier}
                onClick={() => setzen({ in: null })}
                className={cn(PLAETTCHEN, "text-caption gap-1.5 px-2", !ansicht.nurHier ? PLAETTCHEN_AN : PLAETTCHEN_AUS)}
              >
                Überall
                <span className="numeric text-body-sm text-text-primary font-medium">{formatDecimal(trefferUeberall)}</span>
              </button>
              <button
                type="button"
                aria-pressed={ansicht.nurHier}
                onClick={() => setzen({ in: "hier" })}
                className={cn(PLAETTCHEN, "text-caption gap-1.5 px-2", ansicht.nurHier ? PLAETTCHEN_AN : PLAETTCHEN_AUS)}
              >
                <span className="max-w-[9rem] truncate">In „{ordnerName}“</span>
                <span className="numeric text-body-sm text-text-primary font-medium">{formatDecimal(trefferHier)}</span>
              </button>
            </Schiene>
          )}

          <Luecke />

          <Zaehlschiene titel="Art">
            <Zaehlknopf label="Alle" sammel anzahl={gesamt} aktiv={!ansicht.art} titel="Alle Dateiarten" onClick={() => setzen({ art: null })} />
            <Fuge />
            {ART_REIHE.filter((a) => (artZaehler.get(a) ?? 0) > 0 || ansicht.art === a).map((a) => (
              <Zaehlknopf
                key={a}
                label={DATEIARTEN[a].mehrzahl}
                anzahl={artZaehler.get(a) ?? 0}
                aktiv={ansicht.art === a}
                titel={DATEIARTEN[a].hinweis}
                onClick={() => setzen({ art: ansicht.art === a ? null : a })}
              />
            ))}
          </Zaehlschiene>

          <Select
            aria-label="Sortierung"
            value={ansicht.sort}
            onChange={(s) => setzen({ sort: s === "name" ? null : s })}
            options={SORTIERUNGEN}
            className="h-9 w-44 shrink-0"
          />

          <Schiene>
            <button
              type="button"
              aria-pressed={darstellung === "raster"}
              aria-label="Als Raster"
              title="Als Raster"
              onClick={() => setzen({ ansicht: "raster" })}
              className={cn(PLAETTCHEN, "w-8 justify-center", darstellung === "raster" ? PLAETTCHEN_AN : PLAETTCHEN_AUS)}
            >
              <LayoutGrid className="h-3.5 w-3.5" aria-hidden />
            </button>
            <button
              type="button"
              aria-pressed={darstellung === "liste"}
              aria-label="Als Liste"
              title="Als Liste"
              onClick={() => setzen({ ansicht: "liste" })}
              className={cn(PLAETTCHEN, "w-8 justify-center", darstellung === "liste" ? PLAETTCHEN_AN : PLAETTCHEN_AUS)}
            >
              <List className="h-3.5 w-3.5" aria-hidden />
            </button>
          </Schiene>
        </>
      }
    />
  );
}
